/**
 * VisualizerRenderer — Renders HTML/SVG visualizations in a sandboxed iframe.
 *
 * Content comes from ```visualizer code fences in the LLM output.
 * CSS variables from the design system are injected for theme consistency.
 * The iframe auto-sizes to its content via ResizeObserver + postMessage.
 *
 * Security: sandbox="allow-scripts" only — no same-origin, no forms, no popups.
 * No interactivity — visualizations are static.
 *
 * Export: PNG capture via postMessage protocol — iframe loads html2canvas from CDN,
 * renders its own body to canvas, and sends the dataURL back to the parent.
 */

import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { Copy, Check, Maximize2, Minimize2, Download } from 'lucide-react';
import { getThemeCSS } from './getThemeCSS';
import { triggerBlobDownload } from '@/utils/blobDownload';
import type { VisualizerRendererProps } from './types';

// CDN allowlist: the LLM generates content that loads Chart.js, D3, etc. from these CDNs.
// Without allow-same-origin, the iframe cannot access parent cookies/storage.
const CSP_META = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline' https://cdnjs.cloudflare.com https://esm.sh https://cdn.jsdelivr.net https://unpkg.com; style-src 'unsafe-inline'; img-src data: https:;">`;

const CAPTURE_TIMEOUT_MS = 8000;

/**
 * Script injected into the iframe to handle PNG capture requests from parent.
 * Uses native Canvas API — no external dependencies.
 * Detects SVG elements (D3, custom) and canvas elements (Chart.js) in the DOM.
 */
const captureScript = `
<script>
  window.addEventListener('message', async function(e) {
    if (e.origin !== PARENT_ORIGIN) return;
    if (!e.data || e.data.type !== 'capture') return;
    try {
      var scale = 2;
      var dataUrl = null;

      // Strategy 1: If there are canvas elements (Chart.js), export the largest one
      var canvases = document.querySelectorAll('canvas');
      if (canvases.length > 0) {
        var mainCanvas = canvases[0];
        for (var i = 1; i < canvases.length; i++) {
          if (canvases[i].width * canvases[i].height > mainCanvas.width * mainCanvas.height) {
            mainCanvas = canvases[i];
          }
        }
        // Re-draw onto a white-background canvas
        var out = document.createElement('canvas');
        out.width = mainCanvas.width;
        out.height = mainCanvas.height;
        var outCtx = out.getContext('2d');
        outCtx.fillStyle = '#ffffff';
        outCtx.fillRect(0, 0, out.width, out.height);
        outCtx.drawImage(mainCanvas, 0, 0);
        dataUrl = out.toDataURL('image/png');
      }

      // Strategy 2: If there are SVG elements (D3, custom SVG), serialize and draw
      if (!dataUrl) {
        var svgs = document.querySelectorAll('svg');
        if (svgs.length > 0) {
          var mainSvg = svgs[0];
          for (var j = 1; j < svgs.length; j++) {
            var r1 = mainSvg.getBoundingClientRect();
            var r2 = svgs[j].getBoundingClientRect();
            if (r2.width * r2.height > r1.width * r1.height) mainSvg = svgs[j];
          }
          if (!mainSvg.getAttribute('xmlns')) {
            mainSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
          }
          // Inline computed styles for accurate rendering
          var allEls = mainSvg.querySelectorAll('*');
          for (var k = 0; k < allEls.length; k++) {
            var cs = window.getComputedStyle(allEls[k]);
            var fill = cs.getPropertyValue('fill');
            var stroke = cs.getPropertyValue('stroke');
            var fontSize = cs.getPropertyValue('font-size');
            var fontFamily = cs.getPropertyValue('font-family');
            if (fill) allEls[k].style.fill = fill;
            if (stroke) allEls[k].style.stroke = stroke;
            if (fontSize) allEls[k].style.fontSize = fontSize;
            if (fontFamily) allEls[k].style.fontFamily = fontFamily;
          }
          var serializer = new XMLSerializer();
          var svgStr = serializer.serializeToString(mainSvg);
          var rect = mainSvg.getBoundingClientRect();
          var w = rect.width || 800;
          var h = rect.height || 600;

          dataUrl = await new Promise(function(resolve, reject) {
            var img = new Image();
            img.onload = function() {
              var c = document.createElement('canvas');
              c.width = w * scale;
              c.height = h * scale;
              var ctx = c.getContext('2d');
              ctx.fillStyle = '#ffffff';
              ctx.fillRect(0, 0, c.width, c.height);
              ctx.drawImage(img, 0, 0, c.width, c.height);
              resolve(c.toDataURL('image/png'));
            };
            img.onerror = function() { reject(new Error('SVG image load failed')); };
            img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgStr)));
          });
        }
      }

      // Strategy 3: Fallback — capture body via foreignObject SVG
      if (!dataUrl) {
        var body = document.body;
        var bw = body.scrollWidth || 800;
        var bh = body.scrollHeight || 600;
        var bodyHtml = body.innerHTML;
        var styles = '';
        var styleEls = document.querySelectorAll('style');
        for (var s = 0; s < styleEls.length; s++) styles += styleEls[s].outerHTML;

        var svgData = '<svg xmlns="http://www.w3.org/2000/svg" width="' + bw + '" height="' + bh + '">' +
          '<foreignObject width="100%" height="100%">' +
          '<div xmlns="http://www.w3.org/1999/xhtml">' + styles +
          '<div style="width:' + bw + 'px">' + bodyHtml + '</div></div>' +
          '</foreignObject></svg>';

        dataUrl = await new Promise(function(resolve, reject) {
          var img2 = new Image();
          img2.onload = function() {
            var c2 = document.createElement('canvas');
            c2.width = bw * scale;
            c2.height = bh * scale;
            var ctx2 = c2.getContext('2d');
            ctx2.fillStyle = '#ffffff';
            ctx2.fillRect(0, 0, c2.width, c2.height);
            ctx2.drawImage(img2, 0, 0, c2.width, c2.height);
            resolve(c2.toDataURL('image/png'));
          };
          img2.onerror = function() { reject(new Error('foreignObject capture failed')); };
          img2.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgData);
        });
      }

      if (dataUrl) {
        window.parent.postMessage({ type: 'capture_result', dataUrl: dataUrl }, PARENT_ORIGIN);
      } else {
        window.parent.postMessage({ type: 'capture_error', error: 'No capturable content found' }, PARENT_ORIGIN);
      }
    } catch (err) {
      window.parent.postMessage({ type: 'capture_error', error: String(err) }, PARENT_ORIGIN);
    }
  });
</script>
`;

/** Converts a data URL to a Blob. */
function dataUrlToBlob(dataUrl: string): Blob {
  const parts = dataUrl.split(',');
  const header = parts[0] ?? '';
  const base64 = parts[1] ?? '';
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/png';
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

export const VisualizerRenderer: React.FC<VisualizerRendererProps> = ({ content }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const iframeContent = useMemo(() => {
    const themeCSS = getThemeCSS();
    const parentOrigin = window.location.origin;
    const resizeScript = `
      <script>
        var PARENT_ORIGIN = '${parentOrigin}';
        const ro = new ResizeObserver(() => {
          const h = document.documentElement.scrollHeight;
          window.parent.postMessage({ type: 'resize', height: h }, PARENT_ORIGIN);
        });
        ro.observe(document.documentElement);
      </script>
    `;
    return `<!DOCTYPE html>
<html><head>${CSP_META}<style>${themeCSS}</style></head>
<body>${content}${resizeScript}${captureScript}</body></html>`;
  }, [content]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      if (!event.data || typeof event.data !== 'object') return;
      if (event.data.type === 'resize' && typeof event.data.height === 'number' && event.data.height > 0 && event.data.height <= 10000) {
        iframeRef.current.style.height = `${event.data.height}px`;
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  /** Requests a PNG capture from the iframe and returns the Blob. */
  const captureIframe = useCallback((): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      if (!iframeRef.current?.contentWindow) {
        reject(new Error('Iframe not available'));
        return;
      }

      const timeout = setTimeout(() => {
        window.removeEventListener('message', handler);
        reject(new Error('Capture timeout'));
      }, CAPTURE_TIMEOUT_MS);

      function handler(event: MessageEvent) {
        if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
        if (!event.data || typeof event.data !== 'object') return;

        if (event.data.type === 'capture_result' && typeof event.data.dataUrl === 'string') {
          clearTimeout(timeout);
          window.removeEventListener('message', handler);
          resolve(dataUrlToBlob(event.data.dataUrl));
        } else if (event.data.type === 'capture_error') {
          clearTimeout(timeout);
          window.removeEventListener('message', handler);
          reject(new Error(event.data.error ?? 'Capture failed'));
        }
      }

      window.addEventListener('message', handler);
      // '*' required: srcdoc iframes have null origin, no specific targetOrigin is accepted.
      // Security: the iframe has sandbox="allow-scripts" (no allow-same-origin) and the
      // message handler above validates event.source === iframeRef.current.contentWindow.
      iframeRef.current.contentWindow.postMessage({ type: 'capture' }, '*');
    });
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      const blob = await captureIframe();
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob }),
        ]);
      }
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (e) {
      console.error('[VisualizerRenderer] copy failed:', e);
    }
  }, [captureIframe]);

  const handleDownload = useCallback(async () => {
    try {
      const blob = await captureIframe();
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
      triggerBlobDownload(blob, `visualisation-${timestamp}.png`);
    } catch (e) {
      console.error('[VisualizerRenderer] download failed:', e);
    }
  }, [captureIframe]);

  if (!content) return null;

  const iframeElement = (
    <iframe
      ref={iframeRef}
      srcDoc={iframeContent}
      sandbox="allow-scripts"
      className="w-full border-0"
      style={{ minHeight: '100px', maxHeight: isExpanded ? 'none' : '800px' }}
      title="Visualisation"
    />
  );

  const actionButtons = (compact = false) => (
    <>
      {!compact && (
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border bg-white border-grey-200 text-grey-600 hover:bg-grey-100 hover:text-grey-800 transition-all duration-200"
        >
          <Maximize2 className="h-3 w-3" />
          <span>Agrandir</span>
        </button>
      )}
      <button
        type="button"
        onClick={handleDownload}
        className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border bg-white border-grey-200 text-grey-600 hover:bg-grey-100 hover:text-grey-800 transition-all duration-200"
      >
        <Download className="h-3 w-3" />
        <span>Télécharger</span>
      </button>
      <button
        type="button"
        onClick={handleCopy}
        className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border transition-all duration-200
          ${isCopied
            ? 'bg-grey-100 border-grey-200 text-grey-800'
            : 'bg-white border-grey-200 text-grey-600 hover:bg-grey-100 hover:text-grey-800'
          }`}
      >
        {isCopied ? <><Check className="h-3 w-3" /><span>Copié !</span></> : <><Copy className="h-3 w-3" /><span>Copier</span></>}
      </button>
    </>
  );

  if (isExpanded) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="relative w-[90vw] max-w-5xl max-h-[85vh] rounded-xl border border-grey-200 bg-white shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-grey-100 bg-grey-50/80">
            <span className="text-xs font-semibold text-grey-600 uppercase tracking-wider">Visualisation</span>
            <div className="flex items-center gap-1.5">
              {actionButtons(true)}
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border bg-white border-grey-200 text-grey-600 hover:bg-grey-100 hover:text-grey-800 transition-all duration-200"
              >
                <Minimize2 className="h-3 w-3" />
                <span>Réduire</span>
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto">
            {iframeElement}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-4 rounded-lg border border-grey-200 bg-white overflow-hidden">
      {iframeElement}
      <div className="flex justify-end gap-1.5 px-3 py-1.5 border-t border-grey-100 bg-grey-50/50">
        {actionButtons()}
      </div>
    </div>
  );
};
